/**
 * SlideSystem - Handles tangent-based sliding during freeze mode
 * Allows players to adjust piece position by dragging along the collision surface
 */

import Phaser from 'phaser';
import {
  Vector2,
  getTangent,
  normalize,
  dot,
  scale,
  add,
  length,
  clampValue,
} from '../../utils/vectorMath';
import { SLIDE } from '../config/gameConfig';

/**
 * Context for the current slide operation
 */
interface SlideContext {
  body: MatterJS.BodyType;
  collisionNormal: Vector2;
  tangent: Vector2;
  startPosition: Vector2;
}

/**
 * System for handling piece sliding during freeze mode
 */
export class SlideSystem {
  private scene: Phaser.Scene;
  private context: SlideContext | null = null;
  private isDragging: boolean = false;
  private dragStartPointer: Vector2 = { x: 0, y: 0 };

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Start a slide operation for a body that just landed
   */
  startSlide(body: MatterJS.BodyType, collisionNormal: Vector2): void {
    // Normalize the collision normal
    const normalizedNormal = normalize(collisionNormal);

    this.context = {
      body,
      collisionNormal: normalizedNormal,
      tangent: getTangent(normalizedNormal),
      startPosition: { x: body.position.x, y: body.position.y },
    };
  }

  /**
   * Handle pointer down event
   */
  handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.context) return;

    this.isDragging = true;
    this.dragStartPointer = { x: pointer.x, y: pointer.y };
  }

  /**
   * Handle pointer move event
   * Returns true if the piece was moved
   */
  handlePointerMove(
    pointer: Phaser.Input.Pointer,
    placedBodies: MatterJS.BodyType[]
  ): boolean {
    if (!this.context || !this.isDragging) return false;

    // Calculate drag delta
    const dragDelta: Vector2 = {
      x: pointer.x - this.dragStartPointer.x,
      y: pointer.y - this.dragStartPointer.y,
    };

    // Check deadzone
    const dragDistance = length(dragDelta);
    if (dragDistance < SLIDE.DEADZONE) return false;

    // Project drag onto tangent direction
    const normalizedTangent = normalize(this.context.tangent);
    const projectedDistance = dot(dragDelta, normalizedTangent) * SLIDE.SENSITIVITY;

    // Clamp to max slide distance
    const clampedDistance = clampValue(
      projectedDistance,
      -SLIDE.MAX_DISTANCE,
      SLIDE.MAX_DISTANCE
    );

    // Calculate new position
    const offset = scale(normalizedTangent, clampedDistance);
    const newPosition = add(this.context.startPosition, offset);

    // Validate against collisions
    const validatedPosition = this.validatePosition(newPosition, placedBodies);

    // Apply position
    this.scene.matter.body.setPosition(this.context.body, validatedPosition);

    return true;
  }

  /**
   * Handle pointer up event
   * Returns true if a slide was in progress
   */
  handlePointerUp(): boolean {
    if (!this.isDragging) return false;

    this.isDragging = false;
    return true;
  }

  /**
   * End the slide operation
   */
  endSlide(): void {
    this.context = null;
    this.isDragging = false;
  }

  /**
   * Check if currently in a drag operation
   */
  isSliding(): boolean {
    return this.isDragging;
  }

  /**
   * Check if there's an active slide context
   */
  hasContext(): boolean {
    return this.context !== null;
  }

  /**
   * Get the current slide context
   */
  getContext(): SlideContext | null {
    return this.context;
  }

  /**
   * Get the tangent direction for the current slide
   */
  getTangent(): Vector2 | null {
    return this.context?.tangent ?? null;
  }

  /**
   * Validate a position against collision with placed bodies
   * Returns the original position if there would be a collision
   */
  private validatePosition(
    newPosition: Vector2,
    placedBodies: MatterJS.BodyType[]
  ): Vector2 {
    if (!this.context) return newPosition;

    const body = this.context.body;
    const originalPosition = { x: body.position.x, y: body.position.y };

    // Temporarily move body to check collision
    this.scene.matter.body.setPosition(body, newPosition);

    // Check for overlaps with placed bodies
    for (const other of placedBodies) {
      // Skip self
      if (other === body) continue;

      // Check for overlap using bounding box intersection
      const bodyBounds = body.bounds;
      const otherBounds = other.bounds;

      if (bodyBounds && otherBounds) {
        const overlap =
          bodyBounds.min.x < otherBounds.max.x &&
          bodyBounds.max.x > otherBounds.min.x &&
          bodyBounds.min.y < otherBounds.max.y &&
          bodyBounds.max.y > otherBounds.min.y;

        if (overlap) {
          // Revert to original position
          this.scene.matter.body.setPosition(body, originalPosition);
          return originalPosition;
        }
      }
    }

    // Check against game bounds
    const halfWidth = 30; // Approximate shape half-width
    const gameWidth = 390; // GAME.WIDTH
    if (newPosition.x - halfWidth < 0 || newPosition.x + halfWidth > gameWidth) {
      this.scene.matter.body.setPosition(body, originalPosition);
      return originalPosition;
    }

    return newPosition;
  }
}
